import { useMemo, useState } from 'react';
import { DownloadIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Button,
  Combobox,
  DataTable,
  DatePicker,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  StatTile,
  Tabs,
  type DataTableCard,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { saveBlob } from '@/lib/download';
import {
  FLAG_DEFAULT_SORT,
  FLAG_SEARCH_FIELDS,
  FLAG_SORT_KEYS,
  FacilitatorFlagsTable,
} from '@/components/facilitator-flags-table';
import {
  BASIS_LABELS,
  ROLE_FILTER,
  SPEND_CATEGORY_LABELS,
  SPEND_CATEGORY_ORDER,
  SPEND_PARAM,
  TRAVEL_CATEGORIES,
  eventDateLabel,
  facilitatorEventsCsv,
  facilitatorExportFileName,
  facilitatorPersonPath,
  fetchFacilitatorEvents,
  parseRoleFilter,
  parseSpendCategory,
  spendMoney,
  spendShare,
  toFlagRows,
  toPersonRows,
  unitMoney,
  useFacilitatorList,
  useFacilitatorOutliers,
  useSpendParams,
  type FacilitatorList,
  type PersonRow,
  type RoleFilter,
  type TravelCategory,
  type WindowParams,
} from '@/lib/facilitator-spend';

/** The page's two views (`?view=`). */
const SPEND_VIEW = {
  people: 'people',
  outliers: 'outliers',
} as const;
type SpendView = (typeof SPEND_VIEW)[keyof typeof SPEND_VIEW];

function parseView(value: string | null): SpendView {
  return value === SPEND_VIEW.outliers ? SPEND_VIEW.outliers : SPEND_VIEW.people;
}

const PERSON_SORT_FIELDS = {
  name: 'name',
  role: 'roleLabel',
  events: 'events',
  participants: 'participants',
  total: 'total',
  avgFee: 'avgFeePerEvent',
  travel: 'travel',
  flagged: 'flaggedAmount',
} as const;
// Each travel column sorts by its total, which PersonRow keeps under the category key.
const PERSON_SORT_KEYS: readonly string[] = [...Object.values(PERSON_SORT_FIELDS), ...TRAVEL_CATEGORIES];
const PERSON_DEFAULT_SORT = { key: PERSON_SORT_FIELDS.total, direction: 'desc' } as const;
const PERSON_SEARCH_FIELDS = ['name', 'email', 'roleLabel'] as const satisfies ReadonlyArray<keyof PersonRow>;

const ROLE_OPTIONS: ReadonlyArray<{ key: RoleFilter; label: string }> = [
  { key: ROLE_FILTER.all, label: 'Everyone' },
  { key: ROLE_FILTER.facilitator, label: 'Facilitators' },
  { key: ROLE_FILTER.assistant, label: 'Assistants' },
];

/** The columns before the travel breakdown; the numeric ones from Events on are right-aligned. */
const LEADING_COLUMNS = 7;

/** "per line" says nothing, so only meaningful units are spelled out under an average. */
const LINE_BASIS = 'line';

function optionalMoney(value: number | null): string {
  return value === null ? '—' : unitMoney(value);
}

/** A travel total, or a dash when nothing was billed. */
function totalMoney(value: number): string {
  return value === 0 ? '—' : spendMoney(value);
}

/** "$187 per stay" under a total; "per line" says nothing, so it shows the bare average. */
function averageHint(row: PersonRow, category: TravelCategory): string | null {
  const average = row.summary.averages[category];
  if (average.average === null) return null;
  const basis = average.basis && average.basis !== LINE_BASIS ? ` ${BASIS_LABELS[average.basis]}` : ' avg';
  return `${unitMoney(average.average)}${basis}`;
}

function participantsLabel(value: number | null): string {
  return value === null ? '—' : Math.round(value).toLocaleString('en-US');
}

/**
 * Export CSV: every person's events in the window, one row per person per
 * event with each category in its own column. Fetched on click, so the page
 * never loads the long list it does not show.
 */
function ExportButton({ window, role }: { readonly window: WindowParams; readonly role: RoleFilter }): React.ReactElement {
  const [state, setState] = useState<{ readonly busy: boolean; readonly error: string | null }>({ busy: false, error: null });

  async function exportCsv(): Promise<void> {
    setState({ busy: true, error: null });
    try {
      const data = await fetchFacilitatorEvents(window, role);
      saveBlob(new Blob([facilitatorEventsCsv(data.rows)], { type: 'text/csv;charset=utf-8' }), facilitatorExportFileName(data.window));
      setState({ busy: false, error: null });
    } catch (error) {
      setState({ busy: false, error: errorMessage(error) });
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button variant="secondary" disabled={state.busy} onClick={() => void exportCsv()}>
        <DownloadIcon aria-hidden className="size-4" />
        {state.busy ? 'Exporting…' : 'Export CSV'}
      </Button>
      {state.error ? (
        <span role="alert" className="text-portal-micro text-red-700">
          Export failed: {state.error}
        </span>
      ) : null}
    </div>
  );
}

function SummaryTiles({ data }: { readonly data: FacilitatorList }): React.ReactElement {
  const { summary, norms } = data;
  return (
    <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatTile
        label="Total paid"
        value={spendMoney(summary.totalPaid)}
        hint={`${summary.people} people, events ${eventDateLabel(data.window.from)} to ${eventDateLabel(data.window.to)}`}
      />
      <StatTile label="Travel share" value={spendShare(summary.travelShare)} hint={`${spendMoney(summary.travelTotal)} of travel`} />
      <StatTile
        label="Worth a look"
        value={spendMoney(summary.flaggedAmount)}
        hint={`${summary.flaggedCount} lines, ${spendMoney(summary.flaggedExcess)} above the norm`}
      />
      <StatTile
        label="Typical fee"
        value={norms.fee ? spendMoney(norms.fee.median) : '—'}
        hint={norms.fee ? `Median per event, over ${norms.fee.sampleSize.toLocaleString('en-US')} events` : 'No fees in this window'}
      />
    </div>
  );
}

/** The travel norms the flags are measured against, one tile per category. */
function TypicalCosts({ data }: { readonly data: FacilitatorList }): React.ReactElement {
  return (
    <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-9">
      {TRAVEL_CATEGORIES.map((category) => {
        const norm = data.norms[category];
        return (
          <StatTile
            key={category}
            label={`Typical ${SPEND_CATEGORY_LABELS[category].toLowerCase()}`}
            value={norm ? unitMoney(norm.median) : '—'}
            hint={norm ? `Look above ${unitMoney(norm.threshold)} · ${norm.sampleSize.toLocaleString('en-US')} lines` : 'Too few lines for a norm'}
          />
        );
      })}
    </div>
  );
}

function PeopleTable({ rows, linkSearch }: { readonly rows: readonly PersonRow[]; readonly linkSearch: string }): React.ReactElement {
  const { sort, onSort } = useTableSort(PERSON_SORT_KEYS, PERSON_DEFAULT_SORT);
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
  const cards: DataTableCard[] = sorted.map((row) => ({
    key: row.contactId,
    title: (
      <Link to={{ pathname: facilitatorPersonPath(row.contactId), search: linkSearch }} className="text-brand">
        {row.name}
      </Link>
    ),
    subtitle: `${row.roleLabel} · ${row.events} events · ${participantsLabel(row.participants)} participants`,
    fields: [
      { label: 'Total paid', value: spendMoney(row.total) },
      { label: 'Avg fee per event', value: optionalMoney(row.avgFeePerEvent) },
      { label: 'Travel', value: `${spendMoney(row.travel)} (${spendShare(row.travelShare)})` },
      ...TRAVEL_CATEGORIES.filter((category) => row[category] !== 0).map((category) => ({
        label: SPEND_CATEGORY_LABELS[category],
        value: spendMoney(row[category]),
      })),
      { label: 'Worth a look', value: row.flaggedCount > 0 ? `${row.flaggedCount} (${spendMoney(row.flaggedAmount)})` : 'None' },
    ],
  }));
  return (
    <DataTable
      headers={[
        { label: 'Person', sortKey: PERSON_SORT_FIELDS.name },
        { label: 'Role', sortKey: PERSON_SORT_FIELDS.role },
        { label: 'Events', sortKey: PERSON_SORT_FIELDS.events },
        { label: 'Participants', sortKey: PERSON_SORT_FIELDS.participants },
        { label: 'Total paid', sortKey: PERSON_SORT_FIELDS.total },
        { label: 'Avg fee per event', sortKey: PERSON_SORT_FIELDS.avgFee },
        { label: 'Travel', sortKey: PERSON_SORT_FIELDS.travel },
        ...TRAVEL_CATEGORIES.map((category) => ({ label: SPEND_CATEGORY_LABELS[category], sortKey: category })),
        { label: 'Worth a look', sortKey: PERSON_SORT_FIELDS.flagged },
      ]}
      rightAlignedHeaders={Array.from({ length: LEADING_COLUMNS - 2 + TRAVEL_CATEGORIES.length + 1 }, (_, index) => index + 2)}
      sort={sort}
      onSort={onSort}
      minWidth={2280}
      cards={cards}
    >
      {sorted.map((row) => (
        <tr key={row.contactId} className="border-t border-portal-line align-middle">
          <td className="p-3 text-portal-small">
            <Link
              to={{ pathname: facilitatorPersonPath(row.contactId), search: linkSearch }}
              className="block font-bold text-brand hover:underline"
            >
              {row.name}
            </Link>
            <span className="block text-portal-micro text-portal-muted">
              {row.lastEventDate ? `Last event ${eventDateLabel(row.lastEventDate)}` : row.email}
            </span>
          </td>
          <td className="whitespace-nowrap p-3 text-portal-small text-portal-muted">{row.roleLabel}</td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums text-portal-muted">{row.events}</td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">
            <span className="block">{participantsLabel(row.participants)}</span>
            {row.avgParticipants !== null ? (
              <span className="block text-portal-micro text-portal-muted">{participantsLabel(row.avgParticipants)} per event</span>
            ) : null}
          </td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small font-bold tabular-nums text-portal-ink">
            {spendMoney(row.total)}
          </td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">{optionalMoney(row.avgFeePerEvent)}</td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">
            <span className="block">{spendMoney(row.travel)}</span>
            <span className="block text-portal-micro text-portal-muted">{spendShare(row.travelShare)} of total</span>
          </td>
          {TRAVEL_CATEGORIES.map((category) => {
            const hint = averageHint(row, category);
            return (
              <td key={category} className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">
                <span className="block">{totalMoney(row[category])}</span>
                {hint ? <span className="block text-portal-micro text-portal-muted">{hint}</span> : null}
              </td>
            );
          })}
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">
            {row.flaggedCount > 0 ? (
              <>
                <span className="block font-bold text-amber-800">{spendMoney(row.flaggedAmount)}</span>
                <span className="block text-portal-micro text-portal-muted">
                  {row.flaggedCount} {row.flaggedCount === 1 ? 'line' : 'lines'}
                </span>
              </>
            ) : (
              <span className="text-portal-muted">—</span>
            )}
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

/**
 * Analytics → Accounting → Facilitators: what each facilitator and assistant
 * billed (fee, assistants, travel), their average flight, hotel, meals and
 * other travel, and the lines worth a look against the norm for their
 * category and program. From the Salesforce income and expense statements.
 * Behind view_accounting.
 */
export function FacilitatorsPage(): React.ReactElement {
  const { params, window, windowSearch, setParam } = useSpendParams();
  const view = parseView(params.get(SPEND_PARAM.view));
  const role = parseRoleFilter(params.get(SPEND_PARAM.role));
  const category = parseSpendCategory(params.get(SPEND_PARAM.category));
  const program = params.get(SPEND_PARAM.program) ?? '';
  const { search, setSearch } = useUrlSearch();
  const { sort: flagSort, onSort: onFlagSort } = useTableSort(FLAG_SORT_KEYS, FLAG_DEFAULT_SORT);

  const list = useFacilitatorList(window, role);
  const outliers = useFacilitatorOutliers(window, { category, program }, view === SPEND_VIEW.outliers);

  const personRows = useMemo(
    () => filterRows(toPersonRows(list.data?.people ?? []), search, PERSON_SEARCH_FIELDS),
    [list.data, search],
  );
  const flagRows = useMemo(
    () => sortRows(filterRows(toFlagRows(outliers.data?.flags ?? []), search, FLAG_SEARCH_FIELDS), flagSort),
    [outliers.data, search, flagSort],
  );

  function setView(next: SpendView): void {
    // Each view sorts its own columns, so a sort from the other one is dropped.
    setParam({ [SPEND_PARAM.view]: next === SPEND_VIEW.people ? '' : next, sort: '', dir: '' });
  }

  const filtered = search.trim() !== '';
  const outlierFiltered = filtered || category !== '' || program !== '';

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ACCOUNTING"
        title="Facilitators"
        subtitle="What facilitators and assistants billed per event and for travel, and the lines worth a look. From the Salesforce income and expense statements; 2023 and 2024 itemize a little less than 2025."
        action={<ExportButton window={window} role={role} />}
      />

      {list.data ? <SummaryTiles data={list.data} /> : null}

      <Tabs<SpendView>
        label="Facilitator views"
        active={view}
        onChange={setView}
        items={[
          { key: SPEND_VIEW.people, label: 'People', ...(list.data ? { count: list.data.summary.people } : {}) },
          {
            key: SPEND_VIEW.outliers,
            label: 'Worth a look',
            // The flagged list spans every role; the summary only matches it when no role is picked.
            ...(list.data && role === ROLE_FILTER.all ? { count: list.data.summary.flaggedCount } : {}),
          },
        ]}
      />

      <div className="my-4 flex flex-wrap items-end gap-3">
        <SearchInput
          label={view === SPEND_VIEW.people ? 'Search people' : 'Search flagged lines'}
          placeholder={view === SPEND_VIEW.people ? 'Name or email' : 'Person, event, program or line'}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-xs"
        />
        <div>
          <label htmlFor="spend-from" className="mb-1 block text-portal-micro font-bold text-portal-muted">
            Events from
          </label>
          <DatePicker id="spend-from" value={window.from} placeholder="Start" onChange={(value) => setParam({ [SPEND_PARAM.from]: value })} />
        </div>
        <div>
          <label htmlFor="spend-to" className="mb-1 block text-portal-micro font-bold text-portal-muted">
            To
          </label>
          <DatePicker id="spend-to" value={window.to} placeholder="Today" onChange={(value) => setParam({ [SPEND_PARAM.to]: value })} />
        </div>
        {view === SPEND_VIEW.people ? (
          <SegmentedControl<RoleFilter>
            label="Which people to show"
            value={role}
            onChange={(next) => setParam({ [SPEND_PARAM.role]: next === ROLE_FILTER.all ? '' : next })}
            options={ROLE_OPTIONS}
          />
        ) : (
          <>
            <div className="w-48">
              <label htmlFor="spend-category" className="mb-1 block text-portal-micro font-bold text-portal-muted">
                Category
              </label>
              <Combobox
                id="spend-category"
                options={SPEND_CATEGORY_ORDER.map((key) => ({ value: key, label: SPEND_CATEGORY_LABELS[key] }))}
                value={category}
                onChange={(value) => setParam({ [SPEND_PARAM.category]: value })}
                placeholder="All categories"
                noneLabel="All categories"
              />
            </div>
            <div className="w-56">
              <label htmlFor="spend-program" className="mb-1 block text-portal-micro font-bold text-portal-muted">
                Program
              </label>
              <Combobox
                id="spend-program"
                options={(outliers.data?.programs ?? []).map((name) => ({ value: name, label: name }))}
                value={program}
                onChange={(value) => setParam({ [SPEND_PARAM.program]: value })}
                placeholder="All programs"
                noneLabel="All programs"
              />
            </div>
          </>
        )}
      </div>

      {view === SPEND_VIEW.people ? (
        list.isError ? (
          <ErrorNotice title="Facilitator spend unavailable" message={errorMessage(list.error)} />
        ) : list.isPending ? (
          <SkeletonTable label="Loading facilitators…" />
        ) : personRows.length === 0 ? (
          <Panel>
            {filtered ? (
              <EmptyState title="No people match" message="Try a different search." />
            ) : (
              <EmptyState
                title="No facilitator spend in this window"
                message="Facilitation, assistant and travel lines on the income and expense statements show up here, one row per person."
              />
            )}
          </Panel>
        ) : (
          <PeopleTable rows={personRows} linkSearch={windowSearch} />
        )
      ) : (
        <>
          {list.data ? <TypicalCosts data={list.data} /> : null}
          <p className="mb-3 text-portal-small text-portal-muted">
            A line is worth a look when its cost per unit is above twice the median for its category or above the 90th
            percentile, whichever is higher. Programs with 20 or more lines in a category get their own norm. A flag is a
            prompt to check the receipt, not a finding: long flights and multi-night stays cost more.
          </p>
          {outliers.isError ? (
            <ErrorNotice title="Flagged lines unavailable" message={errorMessage(outliers.error)} />
          ) : outliers.isPending ? (
            <SkeletonTable label="Loading flagged lines…" />
          ) : flagRows.length === 0 ? (
            <Panel>
              {outlierFiltered ? (
                <EmptyState title="No flagged lines match" message="Try a different search, category or program." />
              ) : (
                <EmptyState title="Nothing stands out" message="Every line in this window is within the norm for its category." />
              )}
            </Panel>
          ) : (
            <FacilitatorFlagsTable rows={flagRows} sort={flagSort} onSort={onFlagSort} showPerson linkSearch={windowSearch} />
          )}
        </>
      )}
    </section>
  );
}
